import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PriorityScoreBar from "@/components/PriorityScoreBar";
import TaskEditDialog from "@/components/TaskEditDialog";
import TrackerPrompts from "@/components/TrackerPrompts";
import {
  Card, SectionTitle, Pill, DueBadge, StatusBadge, DEPT_LABELS, Loader, EmptyState,
} from "@/components/common";
import {
  AlertTriangle, Zap, Ban, FolderKanban, Flame, Clock, User, Pencil, Lock, Link2, CheckCircle2,
} from "lucide-react";

const COUNTS = [
  { key: "overdue", label: "Overdue", icon: AlertTriangle, tone: "critical" },
  { key: "actionable", label: "Actionable now", icon: Zap, tone: "success" },
  { key: "blocked", label: "Blocked", icon: Ban, tone: "warning" },
  { key: "active_projects", label: "Active projects", icon: FolderKanban, tone: "gold" },
  { key: "compressed_projects", label: "Compressed", icon: Flame, tone: "gold" },
];

const TONE_TEXT = {
  critical: "text-critical",
  success: "text-success",
  warning: "text-warning",
  gold: "text-gold",
  info: "text-info",
};

export default function TodaysDecisions() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/screens/today");
    setData(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) return <Loader label="Ranking today's work" />;

  const c = data.counts;

  return (
    <div className="space-y-8">
      <TrackerPrompts />
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl tracking-tight" data-testid="page-title">Today's Decisions</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Welcome back, {user?.name?.split(" ")[0]}. Here's what matters most right now.
          </p>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {COUNTS.map((k, i) => (
          <motion.div key={k.key} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
            <Card className="p-4" data-testid={`kpi-${k.key}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{k.label}</span>
                <k.icon size={16} className={TONE_TEXT[k.tone]} />
              </div>
              <div className="font-mono-num text-3xl mt-2 tabular" style={{ color: c[k.key] > 0 && (k.tone === "critical") ? "hsl(var(--critical))" : undefined }}>
                {c[k.key]}
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      {c.compressed_projects > 0 && (
        <Card className="p-4 border-gold/40 bg-gold/5" data-testid="compressed-warning">
          <div className="flex items-start gap-3">
            <Flame className="text-gold mt-0.5" size={18} />
            <div>
              <div className="font-medium text-gold">{c.compressed_projects} compressed project(s)</div>
              <div className="text-sm text-muted-foreground">
                Summed lead times exceed the time left to the contractual completion date — these cannot finish on time at standard lead times. Expedite or renegotiate.
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Do this now */}
      <section>
        <SectionTitle icon={Zap} hint={`${data.do_now.length} actionable`}>Do this now</SectionTitle>
        {data.do_now.length === 0 ? (
          <Card className="p-2"><EmptyState icon={CheckCircle2} title="Nothing actionable" subtitle="All actionable tasks are done or blocked." /></Card>
        ) : (
          <div className="space-y-2.5">
            {data.do_now.map((t, i) => (
              <TaskCard key={t.id} task={t} rank={i + 1} onEdit={() => setEditing(t)} />
            ))}
          </div>
        )}
      </section>

      {/* Blocked but critical */}
      <section>
        <SectionTitle icon={Ban} hint="priority ≥ 60, waiting on a predecessor">Blocked but critical</SectionTitle>
        {data.blocked_critical.length === 0 ? (
          <Card className="p-2"><EmptyState icon={CheckCircle2} title="No critical blockers" /></Card>
        ) : (
          <div className="space-y-2.5">
            {data.blocked_critical.map((t) => (
              <BlockedCard key={t.id} task={t} onEdit={() => setEditing(t)} />
            ))}
          </div>
        )}
      </section>

      {editing && (
        <TaskEditDialog task={editing} onClose={() => setEditing(null)} onSaved={load} />
      )}
    </div>
  );
}

function TaskCard({ task, rank, onEdit }) {
  const { canEditDept } = useAuth();
  const editable = canEditDept(task.dept);
  return (
    <Card className="p-4" data-testid="task-row">
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        <div className="flex items-center gap-3 lg:w-[38%] min-w-0">
          <span className="font-mono-num text-sm text-muted-foreground w-6 shrink-0">#{rank}</span>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium truncate">{task.project_name}</span>
              {task.project_compressed && <Pill tone="gold">Compressed</Pill>}
            </div>
            <div className="text-sm text-muted-foreground flex items-center gap-2 mt-0.5">
              <span className="text-foreground/80">{task.stage_label}</span>
              <Pill tone="muted">{DEPT_LABELS[task.dept]}</Pill>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 lg:w-[16%]">
          <DueBadge daysLeft={task.days_left} overdue={task.overdue} />
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Clock size={13} /> {task.planned_hours}h
          </span>
        </div>

        <div className="flex-1 min-w-[180px]">
          <PriorityScoreBar task={task} showLabels />
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <StatusBadge status={task.status} complete={task.is_complete} />
          <button
            onClick={onEdit}
            data-testid={`edit-task-${task.id}`}
            className={`p-2 rounded-md border transition-all ${
              editable ? "border-gold/30 text-gold hover:bg-gold/10" : "border-border text-muted-foreground hover:bg-muted"
            }`}
            title={editable ? "Update task" : "View (read-only for your role)"}
          >
            {editable ? <Pencil size={15} /> : <Lock size={15} />}
          </button>
        </div>
      </div>
    </Card>
  );
}

function BlockedCard({ task, onEdit }) {
  const blockers = task.blockers?.length ? task.blockers : (task.predecessor_stage_label ? [{ stage_label: task.predecessor_stage_label, dept: task.predecessor_dept }] : []);
  return (
    <Card className="p-4 border-warning/25" data-testid="blocked-row">
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        <div className="lg:w-[38%] min-w-0">
          <div className="font-medium truncate">{task.project_name}</div>
          <div className="text-sm text-muted-foreground">{task.stage_label} · {DEPT_LABELS[task.dept]}</div>
        </div>
        <div className="flex items-center gap-2 text-sm lg:w-[34%] flex-wrap">
          <Link2 size={15} className="text-warning shrink-0" />
          <span className="text-muted-foreground">Waiting on</span>
          {blockers.map((b, i) => (
            <span key={i} className="flex items-center gap-1.5">
              <span className="text-foreground font-medium">{b.stage_label}</span>
              <Pill tone="warning">{DEPT_LABELS[b.dept] || b.dept}</Pill>
            </span>
          ))}
        </div>
        <div className="flex-1 min-w-[160px]"><PriorityScoreBar task={task} /></div>
        <div className="shrink-0">
          <button onClick={onEdit} data-testid={`view-blocked-${task.id}`} className="px-3 py-2 rounded-md border border-border text-sm text-muted-foreground hover:bg-muted flex items-center gap-1.5">
            <User size={14} /> Details
          </button>
        </div>
      </div>
    </Card>
  );
}
